import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

const CONTROL =
  'w-full rounded-xl border bg-surface px-3.5 text-sm text-fg shadow-[0_1px_2px_rgb(0_0_0/0.03)] transition placeholder:text-muted/80 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

function Wrapper({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-[13px] font-medium text-fg/80">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p role="alert" className="text-[12px] text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  icon?: LucideIcon;
  /** Rendered inside the control, before the value (e.g. a currency symbol). */
  prefix?: string;
  suffix?: string;
  containerClassName?: string;
};

export function Input({
  label,
  hint,
  error,
  icon: Icon,
  prefix,
  suffix,
  containerClassName,
  className,
  id,
  ...rest
}: InputProps) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <Wrapper
      id={inputId}
      label={label}
      hint={hint}
      error={error}
      className={containerClassName}
    >
      <div
        className={cn(
          'flex h-10 items-center gap-2 rounded-xl border bg-surface px-3.5 shadow-[0_1px_2px_rgb(0_0_0/0.03)] transition',
          error
            ? 'border-danger ring-4 ring-danger/10'
            : 'border-line focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/15'
        )}
      >
        {Icon && <Icon size={15} className="shrink-0 text-muted" />}
        {prefix && <span className="shrink-0 text-sm text-muted">{prefix}</span>}
        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-muted/80',
            className
          )}
          {...rest}
        />
        {suffix && <span className="shrink-0 text-sm text-muted">{suffix}</span>}
      </div>
    </Wrapper>
  );
}

export function Textarea({
  label,
  hint,
  error,
  containerClassName,
  className,
  id,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  containerClassName?: string;
}) {
  const generated = useId();
  const fieldId = id ?? generated;

  return (
    <Wrapper id={fieldId} label={label} hint={hint} error={error} className={containerClassName}>
      <textarea
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cn(
          CONTROL,
          'min-h-20 resize-y py-2.5 outline-none',
          error
            ? 'border-danger ring-4 ring-danger/10'
            : 'border-line focus:border-primary focus:ring-4 focus:ring-primary/15',
          className
        )}
        {...rest}
      />
    </Wrapper>
  );
}

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export function Select({
  label,
  hint,
  error,
  options,
  placeholder,
  containerClassName,
  className,
  id,
  ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  options: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}) {
  const generated = useId();
  const fieldId = id ?? generated;

  return (
    <Wrapper id={fieldId} label={label} hint={hint} error={error} className={containerClassName}>
      <div className="relative">
        <select
          id={fieldId}
          aria-invalid={error ? true : undefined}
          className={cn(
            CONTROL,
            'h-10 appearance-none pr-9 outline-none',
            error
            ? 'border-danger ring-4 ring-danger/10'
            : 'border-line focus:border-primary focus:ring-4 focus:ring-primary/15',
            className
          )}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={15}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
        />
      </div>
    </Wrapper>
  );
}

export function Checkbox({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2.5',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[var(--primary)]"
      />
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {description && <span className="block text-[12px] text-muted">{description}</span>}
      </span>
    </label>
  );
}

/** iOS-style switch, for settings rows. */
export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-full transition',
        checked ? 'bg-primary' : 'border border-line bg-elevated'
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 size-[18px] -translate-y-1/2 rounded-full bg-white shadow transition-all',
          checked ? 'left-[23px]' : 'left-[3px]'
        )}
      />
    </button>
  );
}
